import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { deleteLocation } from 'commons/src/actions/LocationActions';
import { analyticsLogger, PageType } from 'commons/src/analytics';
import { BUILDING_VIEWED_SETTINGS } from 'commons/src/analytics/AnalyticsEvents';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import PageWrapperContent from 'commons/src/components/containers/PageWrapperContent';
import Error from 'commons/src/components/errorComponents/Error';
import { pageNotFoundUrl } from 'commons/src/components/errorComponents/PageNotFound';
import CollapsableSectionHeader from 'commons/src/components/headers/CollapsableSectionHeader';
import DeleteConfirmModal from 'commons/src/components/modals/DeleteConfirmModal';
import { squareTilesPlaceholder } from 'commons/src/components/placeholder';
import { paths, roleRestrictions } from 'commons/src/constants';
import { userRoleAboveRequiredLevel } from 'commons/src/features/authorization/userRoleAboveRequiredLevel';
import { ErrorType } from 'commons/src/models/commonTypeScript';
import { BusinessRequestTypesUsedInCommons } from 'commons/src/reducers/requestReducer';
import { fetchBuilding, fetchLocationLabels, getLocationConfig } from '../../../actions/locationActions';
import { fetchSpaces } from '../../../actions/spaceActions';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import ConfigureDevicesPage from '../configureDevices/ConfigureDevicesPage';
import BuildingThresholds from './BuildingThresholds';
import CustomLabels from './CustomLabels';
import EditBuilding from './EditBuilding';

export const BuildingSettingsComponent = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { buildingId: locationId } = useParams<'buildingId'>() as { buildingId: string };
    const [searchParams] = useSearchParams();
    const openSection = searchParams.get('section') || '';
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loadingBuildings = useSelector((state: Store) => state.requests[RequestType.FetchBuilding]?.loading || false);
    const errorFetchBuildings = useSelector((state: Store) => state.requests[RequestType.FetchBuilding]?.error);
    const labelsLoading = useSelector(
        (state: Store) => state.requests[RequestType.FetchLocationLabels]?.loading || false
    );
    const deleteLocationLoading = useSelector(
        (state: Store) => state.requests[BusinessRequestTypesUsedInCommons.DeleteLocation]?.loading || false
    );
    const deleteLocationError = useSelector(
        (state: Store) => state.requests[BusinessRequestTypesUsedInCommons.DeleteLocation]?.error
    ) as ErrorType | undefined;
    const { loading: loadingUserInfo, selectedGroup } = useSelector((state: Store) => state.userSettings);
    const buildings = useSelector((state: Store) => state.buildings.buildings);

    const loading = loadingUserInfo || loadingBuildings;
    const error = !!errorFetchBuildings;
    const userRole = selectedGroup?.role;
    const building = buildings[locationId];
    const locationError = !loading && !building;

    const [locationFetched, setLocationFetched] = useState(!!building);
    const [buildingSettingsOpen, setBuildingSettingsOpen] = useState(false);
    const [buildingThresholdsOpen, setBuildingThresholdsOpen] = useState(openSection === 'thresholds');
    const [customLabelsOpen, setCustomLabelsOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    useEffect(() => {
        if (deleteLoading && !deleteLocationLoading && !deleteLocationError) {
            navigate(`/${paths.buildings}`);
        }
        if (deleteLoading && !deleteLocationLoading && deleteLocationError) {
            setDeleteLoading(false);
        }
    }, [deleteLocationLoading, deleteLocationError]);

    useEffect(() => {
        if (!building) {
            dispatch(fetchBuilding(locationId));
            dispatch(fetchSpaces(locationId));
        } else analyticsLogger(BUILDING_VIEWED_SETTINGS, { pageType: PageType.Building, buildingName: building.name });
    }, [building, locationId, dispatch]);

    useEffect((): void => {
        dispatch(fetchLocationLabels(locationId));
        dispatch(getLocationConfig(locationId));
    }, [locationId, dispatch]);

    useEffect((): void => {
        if (!locationFetched && building) {
            setLocationFetched(true);
            analyticsLogger(BUILDING_VIEWED_SETTINGS, { pageType: PageType.Building, buildingName: building.name });
        }
    }, [building]);

    useEffect((): void => {
        if (deleteLocationError && !deleteLocationLoading && deleteModalOpen) {
            setDeleteModalOpen(false);
        }
    }, [deleteLocationError]);

    const onDeleteOpenClose = (): void => {
        setDeleteModalOpen(!deleteModalOpen);
    };
    const deleteBuilding = (): void => {
        setDeleteLoading(true);
        dispatch(deleteLocation(locationId));
    };

    useEffect(() => {
        if (locationError && !deleteLoading) {
            navigate(pageNotFoundUrl('buildingNotFound'));
        }
    }, [locationError, deleteLoading]);

    if (error) {
        return <Error />;
    }

    const canDeleteBuilding = userRole && userRoleAboveRequiredLevel(userRole, roleRestrictions.editDeviceOrBuilding);
    const buildingHasCurrentMeasurements = building && building.devices ? building.devices.length > 0 : false;
    const deleteBuildingDescription = buildingHasCurrentMeasurements
        ? txt('Building.MoveDevicesBeforeDeleteBuilding')
        : txt('Building.DeleteBuildingDescription');

    return (
        <div className="page-wrapper__medium" data-building-devices-page>
            {deleteModalOpen && (
                <DeleteConfirmModal
                    title="Building.DeleteBuilding"
                    description={deleteBuildingDescription}
                    onSubmit={deleteBuilding}
                    onCancel={onDeleteOpenClose}
                    onSubmitText="Delete"
                    onCancelText="Close"
                    loading={deleteLocationLoading}
                    hideDelete={buildingHasCurrentMeasurements}
                />
            )}
            <ReactPlaceholder ready={!!building} customPlaceholder={squareTilesPlaceholder}>
                <h2 className="settings__header">{txt('Building.Building')}</h2>
                <div className="settings-details-container settings-details-container--margin">
                    <CollapsableSectionHeader
                        openSection={setBuildingSettingsOpen}
                        headerIsOpen={buildingSettingsOpen}
                        headerText="Building.BuildingSettings"
                        subtext="Building.BuildingSettingsSubText"
                        testAttr="building-settings"
                    />
                    {buildingSettingsOpen && (
                        <div className="page-wrapper__inner page-wrapper__inner--slim">
                            <EditBuilding
                                closeOnSave={false}
                                onClose={(): void => setBuildingSettingsOpen(false)}
                                building={building}
                                locationId={locationId}
                            />
                        </div>
                    )}
                </div>
                <div className="settings-details-container settings-details-container--margin">
                    <CollapsableSectionHeader
                        openSection={setBuildingThresholdsOpen}
                        headerIsOpen={buildingThresholdsOpen}
                        headerText="ThresholdSettings.SensorThresholds"
                        subtext="ThresholdSettings.SensorThresholdsSubText"
                        testAttr="sensor-thresholds"
                    />
                    {buildingThresholdsOpen && (
                        <PageWrapperContent pageType="full">
                            <BuildingThresholds locationId={locationId} />
                        </PageWrapperContent>
                    )}
                </div>
                <div className="settings-details-container settings-details-container--margin">
                    <CollapsableSectionHeader
                        openSection={setCustomLabelsOpen}
                        headerIsOpen={customLabelsOpen}
                        headerText="CustomLabels.CustomLabels"
                        subtext="CustomLabels.CustomLabelsSubText"
                        loading={customLabelsOpen && labelsLoading}
                    />
                    {customLabelsOpen && !labelsLoading && (
                        <div className="page-wrapper__inner page-wrapper__inner--slim">
                            <CustomLabels locationId={locationId} />
                        </div>
                    )}
                </div>
                <ConfigureDevicesPage building={building} />
                {canDeleteBuilding && (
                    <div className="change-location__form__buttons">
                        <PrimaryButton onClick={onDeleteOpenClose} color="alert" title="Delete" />
                    </div>
                )}
            </ReactPlaceholder>
        </div>
    );
};

export default BuildingSettingsComponent;
